import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Popconfirm, Select, Table, Tooltip, message } from "antd";
import { DisconnectOutlined, PlusCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { assignSurveyTemplateToFacility, getSurveyTemplates, getSurveyTemplatesByFacility, unassignSurveyTemplateFromFacility } from "./educationApi";
import OrgFacilityFilter from "./OrgFacilityFilter";
import { SURVEY_DATE_FORMAT, getSurveyClientId, getSurveyList, isSurveyOk, notifySurveyResult, scrollToTopOnTableChange, useColumnSearch } from "./surveyUtils";

const { Option } = Select;

const labelStyle = { marginBottom: 4, fontSize: 13, color: "#5B6472" };

// Ported from the old app's surveyFacilityAssignment/index.js — assign active
// survey templates to one or more facilities (with "Select all") and unassign
// them again.
const SurveyFacilityAssignments = () => {
  const columnSearch = useColumnSearch();

  const [orgId, setOrgId] = useState(undefined);
  const [facilityIds, setFacilityIds] = useState([]);
  const [facilityNames, setFacilityNames] = useState({});
  const [templateId, setTemplateId] = useState(undefined);
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [unassigningId, setUnassigningId] = useState(null);
  const latestFacilityRef = useRef(undefined);

  useEffect(() => {
    (async () => {
      const response = await getSurveyTemplates({ client_id: getSurveyClientId() });
      setTemplates(getSurveyList(response));
    })();
  }, []);

  const loadAssignments = useCallback(async (targetFacilityIds) => {
    latestFacilityRef.current = targetFacilityIds;
    if (!targetFacilityIds || !targetFacilityIds.length) return;
    setLoading(true);
    // Active templates assigned to each selected facility — drives the assignments table.
    const lists = await Promise.all(targetFacilityIds.map(async (id) => {
      const response = await getSurveyTemplatesByFacility(id, { client_id: getSurveyClientId() });
      return getSurveyList(response).map((assignment) => ({ ...assignment, facility_id: id }));
    }));
    if (latestFacilityRef.current !== targetFacilityIds) return; // selection changed meanwhile
    setAssignments(lists.flat());
    setLoading(false);
  }, []);

  const handleFacilitiesLoaded = useCallback((list) => {
    setFacilityNames(Object.fromEntries(list.map((f) => [f.facility_id ?? f.id, f.facility_name ?? f.name])));
  }, []);

  const handleOrgChange = useCallback((value) => {
    latestFacilityRef.current = undefined;
    setLoading(false);
    setOrgId(value);
    setFacilityIds([]);
    setAssignments([]);
  }, []);

  const handleFacilityChange = (value) => {
    const ids = Array.isArray(value) ? value : [];
    setFacilityIds(ids);
    setAssignments([]);
    loadAssignments(ids);
  };

  // the selected facilities that already have a template
  const facilitiesWith = (id) => new Set(assignments.filter((a) => (a.survey_template_id || a.template_id) === id).map((a) => a.facility_id));

  const handleAssign = async () => {
    if (!orgId) return message.error("Please select a group.");
    if (!facilityIds.length) return message.error("Please select a facility.");
    if (!templateId) return message.error("Please select a template.");

    const already = facilitiesWith(templateId);
    const targets = facilityIds.filter((id) => !already.has(id));
    setAssigning(true);
    const responses = [];
    for (const id of targets) {
      responses.push(await assignSurveyTemplateToFacility({ survey_template_id: templateId, facility_id: id, client_id: getSurveyClientId() }));
    }
    setAssigning(false);

    const failed = responses.filter((response) => !isSurveyOk(response));
    if (responses.length === 1) {
      notifySurveyResult(responses[0], "Survey assigned.");
    } else if (failed.length) {
      message.error(`The survey could not be assigned to ${failed.length} of ${responses.length} facilities: ${failed[0]?.result?.message || "Something went wrong"}`);
    } else {
      message.success(`Survey assigned to ${responses.length} facilities.`);
    }
    if (failed.length < responses.length) setTemplateId(undefined);
    loadAssignments(facilityIds);
  };

  const handleUnassign = async (record) => {
    const assignmentId = record.assignment_id !== undefined ? record.assignment_id : record.id;

    if (!assignmentId) {
      message.error("Something went wrong. Please try again.");
      return;
    }

    setUnassigningId(assignmentId);
    const response = await unassignSurveyTemplateFromFacility(assignmentId);
    setUnassigningId(null);

    if (notifySurveyResult(response, "Survey unassigned.")) loadAssignments(facilityIds);
  };

  const columns = [
    {
      title: "Facility",
      dataIndex: "facility_name",
      key: "facility_name",
      width: "20%",
    },
    {
      title: "Template Name",
      dataIndex: "template_name",
      key: "template_name",
      width: "30%",
      ...columnSearch("template_name", "Template name"),
    },
    {
      title: "Survey Type",
      dataIndex: "survey_type",
      key: "survey_type",
      width: "20%",
      ...columnSearch("survey_type", "Survey type"),
    },
    {
      title: "Assigned On",
      dataIndex: "assigned_date",
      key: "assigned_date",
      width: "15%",
      align: "center",
      render: (value) => (value ? moment(value).format(SURVEY_DATE_FORMAT) : "-"),
    },
    {
      title: "Action",
      key: "action",
      width: "15%",
      align: "center",
      render: (_, record) => {
        const assignmentId = record.assignment_id !== undefined ? record.assignment_id : record.id;
        return (
          <Popconfirm title="Unassign this survey from the facility?" okText="Unassign" okType="danger" onConfirm={() => handleUnassign(record)}>
            <Tooltip title="Unassign">
              <Button size="small" danger icon={<DisconnectOutlined />} aria-label="Unassign" loading={unassigningId !== null && unassigningId === assignmentId} />
            </Tooltip>
          </Popconfirm>
        );
      },
    },
  ];

  const dataSource = assignments.map((assignment) => ({
    ...assignment,
    template_name: assignment.template_name || assignment.name || "-",
    survey_type: assignment.survey_type || "-",
    facility_name: facilityNames[assignment.facility_id] || "-",
  }));

  // a template is greyed out only when every selected facility already has it
  const assignedEverywhere = (id) => facilityIds.length > 0 && facilitiesWith(id).size >= facilityIds.length;

  return (
    <div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
        <OrgFacilityFilter orgId={orgId} onOrgChange={handleOrgChange} facilityId={facilityIds} onFacilityChange={handleFacilityChange} facilityMode="multiple" facilityWidth={280} onFacilitiesLoaded={handleFacilitiesLoaded} />
        <div>
          <div style={labelStyle}>Survey Template</div>
          <Select showSearch optionFilterProp="children" placeholder="Select survey template" style={{ width: 260 }} value={templateId} onChange={setTemplateId} allowClear>
            {templates
              .filter((template) => Number(template.is_active) !== 0)
              .map((template) => (
                <Option key={template.id} value={template.id} disabled={assignedEverywhere(template.id)}>
                  {template.name}
                </Option>
              ))}
          </Select>
        </div>
        <Button type="primary" icon={<PlusCircleOutlined />} loading={assigning} onClick={handleAssign}>
          Assign
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey={(record) => `${record.facility_id}-${record.assignment_id || record.id}`}
        loading={loading}
        locale={{ emptyText: facilityIds.length ? "No surveys assigned to the selected facilities yet." : "Please select one or more facilities to view their assigned surveys." }}
        onChange={scrollToTopOnTableChange}
      />
    </div>
  );
};

export default SurveyFacilityAssignments;
